import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { Clock, RefreshCw, Download, Tv, ArrowLeft, Sun, Moon, Users, Target, Gauge, X } from 'lucide-react';
import { getHourlyStaffReport, saveHourlyTargets } from '@/services/payrollApi';

const TV_REFRESH_MS = 60000;

const todayIso = () => {
  const now = new Date();
  const ist = new Date(now.getTime() + (now.getTimezoneOffset() + 330) * 60000);
  return ist.toISOString().slice(0, 10);
};

const fmtQty = (q) => (q ? (Number.isInteger(q) ? q : q.toFixed(1)) : '');

const STATUS_BG = {
  normal: {
    green: 'bg-green-200 text-green-900 font-semibold',
    amber: 'bg-amber-200 text-amber-900 font-semibold',
    red: 'bg-red-300 text-red-900 font-bold',
  },
  tv: {
    green: 'bg-green-500/30 text-green-200 font-semibold',
    amber: 'bg-amber-500/30 text-amber-100 font-semibold',
    red: 'bg-red-500/45 text-red-100 font-bold',
  },
};

// Encode/decode the single filter dropdown value.
const scopeOf = (module, operation) =>
  (operation && operation !== 'all') ? `op:${operation}`
    : (module && module !== 'all') ? `mod:${module}` : 'all';

// lockModule: module dashboards open the report locked to their own operations.
const HourlyStaffReport = ({ lockModule = null, backRoute = '/sections/accounts', backLabel = 'Accounts' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const tvMode = searchParams.get('tv') === '1';

  const [date, setDate] = useState(searchParams.get('date') || todayIso());
  const [module, setModule] = useState(lockModule || searchParams.get('module') || 'all');
  const [operation, setOperation] = useState(searchParams.get('operation') || 'all');
  const [hlMode, setHlMode] = useState(searchParams.get('hl') === 'auto' ? 'auto' : 'target');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(null);
  const [clock, setClock] = useState(new Date());
  const [targetsOpen, setTargetsOpen] = useState(false);
  const [targetDraft, setTargetDraft] = useState({});
  const [savingTargets, setSavingTargets] = useState(false);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setError('');
      const res = await getHourlyStaffReport({ date, module, operation });
      setData(res);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Hourly report load failed', e);
      setError(e?.response?.data?.detail || 'Failed to load the hourly report.');
    } finally {
      setLoading(false);
    }
  }, [date, module, operation]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  useEffect(() => {
    timerRef.current = setInterval(load, TV_REFRESH_MS);
    return () => clearInterval(timerRef.current);
  }, [load]);

  useEffect(() => {
    if (!tvMode) return undefined;
    const c = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(c);
  }, [tvMode]);

  const onScopeChange = (val) => {
    if (val === 'all') { setModule(lockModule || 'all'); setOperation('all'); }
    else if (val.startsWith('mod:')) { setModule(lockModule || val.slice(4)); setOperation('all'); }
    else if (val.startsWith('op:')) { setModule(lockModule || 'all'); setOperation(val.slice(3)); }
  };

  const openTv = () => {
    const url = `${location.pathname}?tv=1&date=${encodeURIComponent(date)}&module=${encodeURIComponent(module)}&operation=${encodeURIComponent(operation)}&hl=${hlMode}`;
    window.open(url, '_blank', 'noopener');
  };

  const openTargets = () => {
    const t = data?.operation_targets || {};
    const draft = {};
    (data?.operations || []).forEach((o) => { draft[o.code] = t[o.code] ?? data?.default_target ?? 150; });
    setTargetDraft(draft);
    setTargetsOpen(true);
  };

  const commitTargets = async () => {
    setSavingTargets(true);
    try {
      const payload = {};
      (data?.operations || []).forEach((o) => { payload[o.code] = Number(targetDraft[o.code]) || 0; });
      await saveHourlyTargets(payload);
      setTargetsOpen(false);
      setLoading(true);
      await load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save targets.');
    } finally {
      setSavingTargets(false);
    }
  };

  const exportCsv = () => {
    if (!data) return;
    const cols = data.columns || [];
    const header = ['Staff ID', 'Staff Name', 'Top Operation', 'Section', 'All Operations', ...cols.map((c) => c.label), 'Day Total', 'Night Total', 'Total', 'Avg/hr'];
    const lines = [header];
    (data.staff || []).forEach((r) => {
      const allOps = (r.ops || []).map((o) => `${o.name}${o.module ? ` (${o.module})` : ''}: ${o.qty}`).join(' | ');
      lines.push([r.staff_id, r.staff_name, r.top_op_name, r.section, allOps, ...cols.map((c) => r.hours?.[c.key] ?? 0), r.day_total, r.night_total, r.total, r.avg_per_hour]);
    });
    lines.push(['', 'TOTAL', '', '', '', ...cols.map((c) => data.column_totals?.[c.key] ?? 0), '', '', data.grand_total, '']);
    const csv = lines.map((row) => row.map((v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    const scopeTag = operation !== 'all' ? operation : module;
    a.download = `hourly_staff_report_${date}_${scopeTag}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const dayCols = data?.day_shift?.columns || [];
  const nightCols = data?.night_shift?.columns || [];
  const staff = data?.staff || [];
  const modules = (data?.modules || []).filter((m) => !lockModule || m === lockModule);
  const operations = data?.operations || [];

  const targetFor = useCallback((row) => {
    if (hlMode === 'auto') return row.avg_per_hour || 0;
    const t = data?.operation_targets?.[row.top_op];
    return t && t > 0 ? t : 0;
  }, [hlMode, data]);

  const statusOf = (q, T) => {
    if (!q || !T || T <= 0) return '';
    if (q >= T) return 'green';
    if (q >= 0.5 * T) return 'amber';
    return 'red';
  };

  const Legend = ({ tv }) => (
    <div className={`flex flex-wrap items-center gap-3 ${tv ? 'text-slate-300 text-sm' : 'text-gray-600 text-xs'}`} data-testid="hourly-legend">
      <span className="font-semibold">{hlMode === 'auto' ? 'Vs each staff\u2019s own avg/hr' : 'Vs per-operation target/hr'}:</span>
      <span className="inline-flex items-center gap-1"><span className={`w-3 h-3 rounded ${tv ? 'bg-green-500/60' : 'bg-green-300'}`} /> On/above</span>
      <span className="inline-flex items-center gap-1"><span className={`w-3 h-3 rounded ${tv ? 'bg-amber-500/60' : 'bg-amber-300'}`} /> Below</span>
      <span className="inline-flex items-center gap-1"><span className={`w-3 h-3 rounded ${tv ? 'bg-red-500/70' : 'bg-red-400'}`} /> Severe &lt;50%</span>
      <span className="inline-flex items-center gap-1"><span className={`w-3 h-3 rounded ${tv ? 'bg-slate-600' : 'bg-gray-200'}`} /> No activity</span>
    </div>
  );

  const Sparkline = () => {
    const cols = data?.columns || [];
    const max = Math.max(1, ...cols.map((c) => data?.column_totals?.[c.key] || 0));
    return (
      <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-3 mb-3" data-testid="hourly-sparkline">
        <div className="flex items-end justify-between gap-1 h-24">
          {cols.map((c) => {
            const v = data?.column_totals?.[c.key] || 0;
            const isDay = (data?.day_shift?.columns || []).some((d) => d.key === c.key);
            return (
              <div key={c.key} className="flex-1 flex flex-col items-center justify-end h-full">
                <span className="text-[10px] text-slate-400 tabular-nums mb-0.5">{v ? fmtQty(v) : ''}</span>
                <div className={`w-full rounded-t ${isDay ? 'bg-amber-400/80' : 'bg-indigo-400/80'}`}
                  style={{ height: `${Math.max(v ? 6 : 0, (v / max) * 100)}%` }} title={`${c.label}: ${fmtQty(v) || 0}`} />
              </div>
            );
          })}
        </div>
        <div className="flex items-center justify-between gap-1 mt-1">
          {cols.map((c) => <div key={c.key} className="flex-1 text-center text-[10px] text-slate-500">{c.label}</div>)}
        </div>
      </div>
    );
  };

  const ScopeSelect = () => (
    <select value={scopeOf(module, operation)} onChange={(e) => onScopeChange(e.target.value)}
      className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 outline-none bg-white max-w-[260px]"
      data-testid="hourly-scope-select">
      {!lockModule && <option value="all">All Sections</option>}
      {modules.map((m) => (
        <optgroup key={m} label={m}>
          <option value={`mod:${m}`}>All {m}</option>
          {operations.filter((o) => o.module === m).map((o) => (
            <option key={o.code} value={`op:${o.code}`}>&nbsp;&nbsp;{o.operation}</option>
          ))}
        </optgroup>
      ))}
    </select>
  );

  const renderTable = (tv) => {
    const headCls = tv ? 'text-sm px-2 py-2' : 'text-xs px-2 py-1.5';
    const cellCls = tv ? 'text-base px-2 py-2' : 'text-xs px-2 py-1.5';
    const dayTint = tv ? 'bg-amber-500/5' : 'bg-amber-50';
    const nightTint = tv ? 'bg-indigo-500/5' : 'bg-indigo-50';
    const dayTotBg = tv ? 'bg-amber-500/20' : 'bg-amber-100';
    const nightTotBg = tv ? 'bg-indigo-500/20' : 'bg-indigo-100';
    const stickyBg = tv ? 'bg-slate-900' : 'bg-white';
    const border = tv ? 'border-slate-700' : 'border-gray-200';
    const statusMap = tv ? STATUS_BG.tv : STATUS_BG.normal;

    const hourCell = (r, c, tint) => {
      const q = r.hours?.[c.key];
      const st = statusOf(q, targetFor(r));
      const bg = q ? (statusMap[st] || '') : `${tint} opacity-40`;
      return <td key={c.key} className={`text-center tabular-nums ${cellCls} border ${border} ${bg}`}>{fmtQty(q) || '·'}</td>;
    };

    return (
      <div className="overflow-auto rounded-lg border" style={{ borderColor: tv ? '#334155' : '#e5e7eb', maxHeight: tv ? '70vh' : '62vh' }}>
        <table className={`min-w-full border-collapse ${tv ? 'text-slate-100' : 'text-gray-800'}`} data-testid="hourly-report-table">
          <thead className="sticky top-0 z-20">
            <tr className={tv ? 'bg-slate-800' : 'bg-gray-100'}>
              <th rowSpan={2} className={`sticky left-0 z-30 ${tv ? 'bg-slate-800' : 'bg-gray-100'} text-left ${headCls} font-bold border ${border}`} style={{ minWidth: tv ? 240 : 190 }}>Staff</th>
              <th colSpan={dayCols.length + 1} className={`text-center ${headCls} font-bold border ${border} ${dayTotBg}`}>
                <span className="inline-flex items-center gap-1"><Sun className="w-4 h-4" /> Day Shift · {data?.day_shift?.window}</span>
              </th>
              <th colSpan={nightCols.length + 1} className={`text-center ${headCls} font-bold border ${border} ${nightTotBg}`}>
                <span className="inline-flex items-center gap-1"><Moon className="w-4 h-4" /> Night Shift · {data?.night_shift?.window}</span>
              </th>
              <th rowSpan={2} className={`text-center ${headCls} font-extrabold border ${border} ${tv ? 'bg-emerald-600/30' : 'bg-emerald-100'}`} style={{ minWidth: 72 }}>Total</th>
            </tr>
            <tr className={tv ? 'bg-slate-800' : 'bg-gray-100'}>
              {dayCols.map((c) => <th key={c.key} className={`text-center ${headCls} font-semibold border ${border} ${dayTint}`} style={{ minWidth: 48 }}>{c.label}</th>)}
              <th className={`text-center ${headCls} font-bold border ${border} ${dayTotBg}`} style={{ minWidth: 58 }}>Day</th>
              {nightCols.map((c) => <th key={c.key} className={`text-center ${headCls} font-semibold border ${border} ${nightTint}`} style={{ minWidth: 48 }}>{c.label}</th>)}
              <th className={`text-center ${headCls} font-bold border ${border} ${nightTotBg}`} style={{ minWidth: 58 }}>Night</th>
            </tr>
          </thead>
          <tbody>
            {staff.map((r, idx) => (
              <tr key={r.staff_id} data-testid={`hourly-row-${r.staff_id}`} className={tv ? (idx % 2 ? 'bg-slate-900/40' : 'bg-slate-900/10') : (idx % 2 ? 'bg-gray-50' : 'bg-white')}>
                <td className={`sticky left-0 z-10 ${stickyBg} ${idx % 2 && !tv ? 'bg-gray-50' : ''} text-left font-medium ${cellCls} border ${border}`} style={{ minWidth: tv ? 240 : 190 }}>
                  <span className="opacity-60 mr-1 text-[10px]">{r.staff_id}</span>{r.staff_name}
                  <span className={`ml-1 ${tv ? 'text-slate-400' : 'text-gray-400'} text-[10px]`}>
                    {hlMode === 'auto' ? `avg ${fmtQty(r.avg_per_hour) || 0}/h` : (r.top_op_name || '')}
                  </span>
                </td>
                {dayCols.map((c) => hourCell(r, c, dayTint))}
                <td className={`text-center tabular-nums font-bold ${cellCls} border ${border} ${dayTotBg}`}>{fmtQty(r.day_total) || '0'}</td>
                {nightCols.map((c) => hourCell(r, c, nightTint))}
                <td className={`text-center tabular-nums font-bold ${cellCls} border ${border} ${nightTotBg}`}>{fmtQty(r.night_total) || '0'}</td>
                <td className={`text-center tabular-nums font-extrabold ${cellCls} border ${border} ${tv ? 'bg-emerald-600/20' : 'bg-emerald-50'}`}>{fmtQty(r.total) || '0'}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="sticky bottom-0 z-20">
            <tr className={tv ? 'bg-slate-800 font-bold' : 'bg-gray-200 font-bold'}>
              <td className={`sticky left-0 z-30 ${tv ? 'bg-slate-800' : 'bg-gray-200'} text-left ${cellCls} border ${border}`}>Column Totals</td>
              {dayCols.map((c) => <td key={c.key} className={`text-center tabular-nums ${cellCls} border ${border}`}>{fmtQty(data?.column_totals?.[c.key]) || '·'}</td>)}
              <td className={`text-center tabular-nums ${cellCls} border ${border} ${dayTotBg}`}>{fmtQty(dayCols.reduce((s, c) => s + (data?.column_totals?.[c.key] || 0), 0)) || '0'}</td>
              {nightCols.map((c) => <td key={c.key} className={`text-center tabular-nums ${cellCls} border ${border}`}>{fmtQty(data?.column_totals?.[c.key]) || '·'}</td>)}
              <td className={`text-center tabular-nums ${cellCls} border ${border} ${nightTotBg}`}>{fmtQty(nightCols.reduce((s, c) => s + (data?.column_totals?.[c.key] || 0), 0)) || '0'}</td>
              <td className={`text-center tabular-nums ${cellCls} border ${border} ${tv ? 'bg-emerald-600/30' : 'bg-emerald-200'}`} data-testid="hourly-grand-total">{fmtQty(data?.grand_total) || '0'}</td>
            </tr>
          </tfoot>
        </table>
      </div>
    );
  };

  const scopeLabel = operation !== 'all'
    ? (operations.find((o) => o.code === operation)?.operation || operation)
    : (module !== 'all' ? module : 'All Sections');

  // ---------- TV MODE ----------
  if (tvMode) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 p-5" data-testid="hourly-staff-report-page">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-xl bg-violet-600"><Clock className="w-7 h-7" /></div>
            <div>
              <h1 className="text-2xl font-extrabold tracking-tight">Hourly Staff Performance</h1>
              <p className="text-slate-400 text-sm">{data?.date} · {scopeLabel} · pieces completed per staff</p>
            </div>
          </div>
          <div className="text-right">
            <div className="text-4xl font-black tabular-nums text-emerald-400">{fmtQty(data?.grand_total) || 0}</div>
            <div className="text-slate-400 text-xs uppercase tracking-widest">Total Pieces</div>
            <div className="text-slate-500 text-xs mt-1">{clock.toLocaleTimeString()} · {staff.length} staff</div>
          </div>
        </div>
        {data && staff.length > 0 && <Sparkline />}
        <div className="mb-3"><Legend tv /></div>
        {loading && !data ? (
          <div className="text-center text-slate-400 py-20 text-xl" data-testid="hourly-loading">Loading…</div>
        ) : staff.length === 0 ? (
          <div className="text-center text-slate-400 py-20 text-xl" data-testid="hourly-empty">No staff activity yet for {data?.date}.</div>
        ) : renderTable(true)}
        {error && <div className="text-red-400 mt-3 text-center">{error}</div>}
      </div>
    );
  }

  // ---------- NORMAL MODE ----------
  return (
    <div className="min-h-screen bg-gray-50" data-testid="hourly-staff-report-page">
      <div className="max-w-[1500px] mx-auto px-4 py-6">
        <button onClick={() => navigate(backRoute)} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 mb-3" data-testid="hourly-back-btn">
          <ArrowLeft className="w-4 h-4" /> {backLabel}
        </button>

        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-violet-600 text-white shadow"><Clock className="w-6 h-6" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900" data-testid="hourly-title">{lockModule ? `${lockModule} — Hourly Report` : 'Hourly Staff Report'}</h1>
              <p className="text-gray-500 text-sm">Pieces completed per staff, by hour — Day &amp; Night shift</p>
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4 flex flex-wrap items-end gap-4 shadow-sm">
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Date</label>
            <input type="date" value={date} max={todayIso()} onChange={(e) => setDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-violet-500 outline-none" data-testid="hourly-date-input" />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Section / Operation</label>
            <ScopeSelect />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-500 mb-1">Pace highlight</label>
            <div className="inline-flex rounded-lg border border-gray-300 overflow-hidden" data-testid="hourly-hl-toggle">
              <button onClick={() => setHlMode('target')} className={`px-3 py-2 text-sm font-medium ${hlMode !== 'auto' ? 'bg-violet-600 text-white' : 'bg-white text-gray-600'}`} data-testid="hourly-hl-section">
                <Target className="w-3.5 h-3.5 inline mr-1" />Operation target
              </button>
              <button onClick={() => setHlMode('auto')} className={`px-3 py-2 text-sm font-medium ${hlMode === 'auto' ? 'bg-violet-600 text-white' : 'bg-white text-gray-600'}`} data-testid="hourly-hl-auto">
                <Gauge className="w-3.5 h-3.5 inline mr-1" />Own average
              </button>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-auto">
            {hlMode !== 'auto' && data?.can_edit_targets !== false && (
              <button onClick={openTargets} disabled={!operations.length} className="inline-flex items-center gap-1.5 bg-white border border-gray-300 text-gray-700 rounded-lg px-3 py-2 text-sm font-medium hover:bg-gray-50 disabled:opacity-40" data-testid="hourly-targets-btn">
                <Target className="w-4 h-4" /> Targets
              </button>
            )}
            <button onClick={() => { setLoading(true); load(); }} className="inline-flex items-center gap-1.5 bg-gray-800 text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-gray-900" data-testid="hourly-refresh-btn">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </button>
            <button onClick={exportCsv} disabled={!staff.length} className="inline-flex items-center gap-1.5 bg-emerald-600 text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-emerald-700 disabled:opacity-40" data-testid="hourly-export-csv-btn">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={openTv} className="inline-flex items-center gap-1.5 bg-violet-600 text-white rounded-lg px-3.5 py-2 text-sm font-medium hover:bg-violet-700" data-testid="hourly-tv-btn">
              <Tv className="w-4 h-4" /> TV Display
            </button>
          </div>
        </div>

        {/* Summary + legend */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 shadow-sm">
            <div className="text-xs text-gray-400">Total Pieces</div>
            <div className="text-xl font-bold text-emerald-600 tabular-nums" data-testid="hourly-summary-total">{fmtQty(data?.grand_total) || 0}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 shadow-sm">
            <div className="text-xs text-gray-400 flex items-center gap-1"><Users className="w-3 h-3" /> Staff</div>
            <div className="text-xl font-bold text-gray-800 tabular-nums">{staff.length}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-2.5 shadow-sm"><Legend /></div>
          {lastRefresh && <div className="text-xs text-gray-400 ml-auto">Updated {lastRefresh.toLocaleTimeString()} · auto every 60s</div>}
        </div>

        {error && <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>}

        {loading && !data ? (
          <div className="text-center text-gray-400 py-20" data-testid="hourly-loading">Loading…</div>
        ) : staff.length === 0 ? (
          <div className="text-center text-gray-400 py-20 bg-white rounded-xl border border-gray-200" data-testid="hourly-empty">
            No staff activity recorded for {data?.date} — {scopeLabel}.
          </div>
        ) : renderTable(false)}
      </div>

      {/* Per-operation Targets editor */}
      {targetsOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="hourly-targets-dialog">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-3 border-b shrink-0">
              <h3 className="font-bold text-gray-900 flex items-center gap-2"><Target className="w-4 h-4 text-violet-600" /> Per-operation pieces/hour target</h3>
              <button onClick={() => setTargetsOpen(false)} className="text-gray-400 hover:text-gray-700"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4 overflow-y-auto">
              <p className="text-xs text-gray-500">Each active hour cell turns green at/above its operation's pace, amber below it, and red below half of it.</p>
              {modules.map((m) => {
                const ops = operations.filter((o) => o.module === m);
                if (!ops.length) return null;
                return (
                  <div key={m}>
                    <div className="text-xs font-bold uppercase tracking-wide text-violet-600 mb-1">{m}</div>
                    <div className="space-y-2">
                      {ops.map((o) => (
                        <div key={o.code} className="flex items-center justify-between gap-3">
                          <label className="text-sm text-gray-700">{o.operation}</label>
                          <input type="number" min="0" value={targetDraft[o.code] ?? ''} onChange={(e) => setTargetDraft((d) => ({ ...d, [o.code]: e.target.value }))}
                            className="w-28 border border-gray-300 rounded-lg px-3 py-1.5 text-sm text-right focus:ring-2 focus:ring-violet-500 outline-none"
                            data-testid={`hourly-target-input-${o.code}`} />
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t shrink-0">
              <button onClick={() => setTargetsOpen(false)} className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50">Cancel</button>
              <button onClick={commitTargets} disabled={savingTargets} className="px-4 py-2 text-sm rounded-lg bg-violet-600 text-white font-medium hover:bg-violet-700 disabled:opacity-50" data-testid="hourly-targets-save">
                {savingTargets ? 'Saving…' : 'Save targets'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HourlyStaffReport;
